import { isDefined, useDictionary } from '@eih-netbroker/common';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { CodeInputModal, CodeInputModalLabels } from '../../../../shared/components/code-input-modal/CodeInputModal';
import { ModalHeader } from '../../../../shared/components/modal/ModalHeader';
import { useAllocation } from '../../services/useAllocation';
import { CashActionStep } from '../../states/cashActionAtoms';
import { CashActionReceivedMessages } from '../common/CashActionReceivedMessages';
import { CashActionSummary } from '../common/CashActionSummary';
import classes from './CashAllocationModal.module.scss';
import { CashAllocationParamEditorWrapper } from './CashAllocationParamEditorWrapper';

type CashAllocationModalProps = {
  onClose: () => void;
};

export const CashAllocationModal = ({ onClose }: CashAllocationModalProps) => {
  const { translation } = useLocale();
  const { formatDictionaryType } = useDictionary();
  const {
    currencyOptions,
    handleModalClose,
    handleTargetAccountChange,
    handleAllocationConfirm,
    handleAllocationSubmit,
    refreshAllocationDetails,
    setCashActionTemp,
    selectedTargetAccount,
    selectedSourceAccount,
    handleSourceAccountChange,
    sourceAccountOptions,
    targetAccountOptions,
    cashActionStep,
    cashActionTemp,
    setCashActionStep,
    selectedAllocationCurrency,
    smsCodeError,
    canRestartTwoFactor,
    handleTwoFactorRestart,
    setSelectedAllocationCurrency,
    allocationDetails,
  } = useAllocation();
  const [amount, setAmount] = useState<string>(cashActionTemp?.amount ?? '1');

  const handleCurrencyChange = useCallback(
    (currency: string) => {
      if (!allocationDetails.isLoaded) {
        return;
      }

      setSelectedAllocationCurrency(currency);
      setAmount('1');
      const firstAvailableSourceAccount = allocationDetails.allocationPaymentItems.find(
        acc => acc.currencyCode === currency,
      );
      handleSourceAccountChange(firstAvailableSourceAccount ?? '');
    },
    [setSelectedAllocationCurrency, allocationDetails, handleSourceAccountChange],
  );

  useEffect(() => {
    refreshAllocationDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init effect
  }, []);

  useEffect(() => {
    if (cashActionStep === CashActionStep.Summary && !cashActionTemp) {
      setCashActionStep(CashActionStep.Params);
    }
  }, [cashActionStep, cashActionTemp, setCashActionStep]);

  const handleSubmit = useCallback(() => {
    if (
      !isDefined(selectedSourceAccount) ||
      !isDefined(selectedTargetAccount) ||
      !isDefined(selectedAllocationCurrency)
    ) {
      return;
    }

    setCashActionTemp({
      amount,
      targetClientId: selectedTargetAccount.clientId ?? '',
      sourceAccountCategory: selectedSourceAccount.accountCategory,
      targetAccountNumber: selectedTargetAccount.accountNumber,
      currencyCode: selectedAllocationCurrency,
      sourceClientId: selectedSourceAccount.clientId,
    });
    setCashActionStep(CashActionStep.Summary);
  }, [
    amount,
    selectedAllocationCurrency,
    selectedSourceAccount,
    selectedTargetAccount,
    setCashActionStep,
    setCashActionTemp,
  ]);

  const numberInputError = useMemo(() => {
    if (isDefined(selectedSourceAccount)) {
      const parsedAmount = parseFloat(amount);

      if (parsedAmount > selectedSourceAccount.amount) {
        return translation.cashAction.paramEditor.error.allocation.cannotExceedSourceAmount;
      }

      if (parsedAmount === 0) {
        return `${translation.cashAction.paramEditor.error.allocation.cannotBeZero} ${selectedAllocationCurrency}!`;
      }
    }
  }, [amount, selectedAllocationCurrency, selectedSourceAccount, translation]);

  const codeInputModalLabels = useMemo<CodeInputModalLabels>(
    () => ({
      title: translation.cashAction.confirm.allocation.title,
      description: translation.cashAction.confirm.description,
      inputLabel: translation.cashAction.confirm.smsCodeLabel,
      cancelLabel: translation.general.cancel,
      submitLabel: translation.cashAction.confirm.allocation.submit,
    }),
    [translation],
  );

  if (cashActionStep === CashActionStep.ReceivedMessages) {
    return (
      <CashActionReceivedMessages
        title={translation.cashAction.allocation.title}
        onClose={() => handleModalClose(onClose)}
      />
    );
  }

  if (cashActionStep === CashActionStep.Confirm) {
    return (
      <CodeInputModal
        error={smsCodeError}
        isInitializing={false}
        onSubmit={async (code: string) => handleAllocationConfirm(code, () => handleModalClose(onClose))}
        onCancel={() => handleModalClose(onClose)}
        onClose={() => handleModalClose(onClose)}
        onRestart={handleTwoFactorRestart}
        canRestart={canRestartTwoFactor}
        labels={codeInputModalLabels}
      />
    );
  }

  if (cashActionStep === CashActionStep.Summary && isDefined(cashActionTemp)) {
    return (
      <CashActionSummary
        title={translation.cashAction.summary.allocation.title}
        submitButtonText={translation.cashAction.summary.allocation.submit}
        amountLabel={translation.cashAction.summary.allocation.amountLabel}
        onClose={() => handleModalClose(onClose)}
        onBack={() => setCashActionStep(CashActionStep.Params)}
        onSubmit={() => handleAllocationSubmit(() => handleModalClose(onClose))}
        sourceAccountText={`${formatDictionaryType(
          cashActionTemp.sourceAccountCategory ?? '',
          'accountCategoryTypes',
        )} - ${cashActionTemp.sourceClientId}`}
        targetAccountText={cashActionTemp.targetAccountNumber}
      />
    );
  }

  return (
    <div className={classes.container}>
      <ModalHeader
        title={translation.cashAction.allocation.title}
        onClose={() => handleModalClose(onClose)}
        isSmall
        isWithDivider
        className={classes.header}
      />
      <CashAllocationParamEditorWrapper
        amount={amount}
        refreshAllocationDetails={refreshAllocationDetails}
        allocationDetails={allocationDetails}
        handleAmountChange={setAmount}
        currencyOptions={currencyOptions}
        handleSubmit={handleSubmit}
        handleTargetAccountChange={handleTargetAccountChange}
        selectedCurrency={selectedAllocationCurrency}
        handleCurrencyChange={handleCurrencyChange}
        handleSourceAccountChange={handleSourceAccountChange}
        numberInputError={numberInputError}
        selectedSourceAccount={selectedSourceAccount}
        selectedTargetAccount={selectedTargetAccount}
        sourceAccountOptions={sourceAccountOptions}
        targetAccountOptions={targetAccountOptions}
      />
    </div>
  );
};
